import Enumerable from "linq";
import {
  IClonable,
  IDateDuration,
} from "../../../base/domain/system";
import { AbstractDocumentEditor } from "../../../base/domain/system/document";
import { IPropertyEditable, IPropertyPresentable } from "../../../base/domain/system/edit";
import { IEditCommand, EditCommandSet, WriteValueCommand } from "../../../base/domain/system/edit/command";
import { IFormatSpecification } from "./IFormatSpecification";
import { IFormatObjectTemporaryImage } from "./IFormatObjectTemporaryImage";
import {
  IEditableFormatStructure,
  IEditableFormatReferenceFile,
  IEditableFormatPageStructure,
  IEditableFormatSetting,
  WriteMethodKinds,
} from "./IEditableFormatStructure";
import { FormatPageStructureEditor } from "./FormatPageStructureEditor";
import { IFormatAlert } from "./IFormatAlert";
import { IEditableFormatQuestionnaire } from ".";

export interface IFormatStructureEditorStore {
  temporaryImages: IFormatObjectTemporaryImage[];
}

export class FormatStructureEditorStorePresenter {
  constructor(protected readonly _source: IFormatStructureEditorStore) {}

  getTemporaryImages(): IFormatObjectTemporaryImage[] {
    return this._source.temporaryImages;
  }
}

export class FormatStructureEditorStoreEditor extends FormatStructureEditorStorePresenter {
  constructor(source: IFormatStructureEditorStore) {
    super(source);
  }

  createPresenter(): FormatStructureEditorStorePresenter {
    return new FormatStructureEditorStorePresenter(this._source);
  }

  setTemporaryImages(source: IFormatObjectTemporaryImage[]): void {
    this._source.temporaryImages = source;
  }
}

export class FormatStructureEditor extends AbstractDocumentEditor<IEditableFormatStructure, IEditableFormatPageStructure, FormatPageStructureEditor> implements IClonable<FormatStructureEditor> {
  constructor(source: IEditableFormatStructure | FormatStructureEditor, specification?: IFormatSpecification) {
    super(source);
    this._specification = (source instanceof FormatStructureEditor)
      ? source._specification
      : specification;
    if (!this._specification) {
      throw new Error("invalid specification");
    }
    this._storeEditor = (source instanceof FormatStructureEditor)
      ? source._storeEditor
      : new FormatStructureEditorStoreEditor({
        temporaryImages: [],
      });
    this._id = this.createPropertyPresenter(s => s.id);
    this._name = this.createPropertyEditor(s => s.name, (s, v) => s.name = v);
    this._documentKindId = this.createPropertyEditor(s => s.documentKindId, (s, v) => s.documentKindId = v);
    this._companyCode = this.createPropertyEditor(s => s.companyCode, (s, v) => s.companyCode = v);
    this._masterCode = this.createPropertyEditor(s => s.masterCode, (s, v) => s.masterCode = v);
    this._expirationDate = this.createPropertyEditor(s => s.expirationDate, (s, v) => s.expirationDate = v);
    this._numberOfPrints = this.createPropertyEditor(s => s.numberOfPrints, (s, v) => s.numberOfPrints = v);
    this._indicationMagnification = this.createPropertyEditor(s => s.indicationMagnification, (s, v) => s.indicationMagnification = v);
    this._categoryType = this.createPropertyEditor(s => s.categoryType, (s, v) => s.categoryType = v);
    this._tag = this.createPropertyEditor(s => s.tag, (s, v) => s.tag = v);
    this._inOutType = this.createPropertyEditor(s => s.inOutType, (s, v) => s.inOutType = v);
    this._referenceFiles = this.createPropertyEditor(s => s.referenceFiles, (s, v) => s.referenceFiles = v);
    this._lastMaxObjectId = this.createPropertyEditor(s => s.lastMaxObjectId, (s, v) => s.lastMaxObjectId = v);
    this._maxPageKey = this.createPropertyPresenter(s => s.maxPageKey);
    this._writeMethodType = this.createPropertyEditor(s => s.writeMethodType, (s, v) => s.writeMethodType = v);
    this._settings = this.createPropertyEditor(s => s.settings, (s, v) => s.settings = v);
    this._questionnaire = this.createPropertyEditor(s => s.questionnaire, (s, v) => s.questionnaire = v);
  }

  private readonly _specification: IFormatSpecification;

  getSpecification(): IFormatSpecification {
    return this._specification;
  }

  private readonly _storeEditor: FormatStructureEditorStoreEditor;

  getStorePresenter(): FormatStructureEditorStorePresenter {
    return this._storeEditor.createPresenter();
  }

  setTemporaryImages(value: IFormatObjectTemporaryImage[]) {
    this._storeEditor.setTemporaryImages(value);
  }

  public createNewPage(source: IEditableFormatPageStructure): FormatPageStructureEditor {
    return new FormatPageStructureEditor(source, this._specification, true);
  }

  protected createPage(source: IEditableFormatPageStructure): FormatPageStructureEditor {
    return new FormatPageStructureEditor(source, this._specification);
  }

  protected createAddPageCommand(page: FormatPageStructureEditor): IEditCommand<IEditableFormatStructure> {
    const nextPageKey = this.maxPageKey().getValue() + 1;
    return new EditCommandSet([
      page.createCommand(),
      this.createUpdatePageKeyCommand(page, nextPageKey),
      this.createUpdateMaxPageKeyCommand(nextPageKey),
    ]);
  }

  private createUpdatePageKeyCommand(page: FormatPageStructureEditor, nextPageKey: number): IEditCommand<IEditableFormatStructure> {
    return new WriteValueCommand<IEditableFormatStructure, number>(
      s => this.getSamePageStructure(s, page).pageKey,
      (s, v) => this.getSamePageStructure(s, page).pageKey = v,
      nextPageKey);
  }

  private getSamePageStructure(source: IEditableFormatStructure, page: FormatPageStructureEditor): IEditableFormatPageStructure {
    return Enumerable.from(source.pages)
      .firstOrDefault(e => e === page.getSource());
  }

  private createUpdateMaxPageKeyCommand(nextPageKey: number): IEditCommand<IEditableFormatStructure> {
    return new WriteValueCommand<IEditableFormatStructure, number>(
      s => s.maxPageKey,
      (s, v) => s.maxPageKey = v,
      nextPageKey);
  }

  protected reconstructPages(): FormatPageStructureEditor[] {
    const editors = this.getPages();
    return Enumerable.from(this.getOrDefault(s => s.pages, []))
      .select(p => {
        const found = Enumerable.from(editors)
          .firstOrDefault(e => e.getSource() === p);
        return found
          ? found.reconstruct()
          : this.createPage(p);
      })
      .toArray();
  }

  private readonly _id: IPropertyPresentable<number>;

  id(): IPropertyPresentable<number> {
    return this._id;
  }

  private readonly _name: IPropertyEditable<string>;

  name(): IPropertyEditable<string> {
    return this._name;
  }

  private readonly _documentKindId: IPropertyEditable<number>;

  documentKindId(): IPropertyEditable<number> {
    return this._documentKindId;
  }

  private readonly _companyCode: IPropertyEditable<string>;

  companyCode(): IPropertyEditable<string> {
    return this._companyCode;
  }

  private readonly _masterCode: IPropertyEditable<string>;

  masterCode(): IPropertyEditable<string> {
    return this._masterCode;
  }

  private readonly _expirationDate: IPropertyEditable<IDateDuration>;

  expirationDate(): IPropertyEditable<IDateDuration> {
    return this._expirationDate;
  }

  private readonly _numberOfPrints: IPropertyEditable<number>;

  numberOfPrints(): IPropertyEditable<number> {
    return this._numberOfPrints;
  }

  private readonly _indicationMagnification: IPropertyEditable<number>;

  indicationMagnification(): IPropertyEditable<number> {
    return this._indicationMagnification;
  }

  private readonly _categoryType: IPropertyEditable<string>;

  categoryType(): IPropertyEditable<string> {
    return this._categoryType;
  }

  private readonly _tag: IPropertyEditable<string>;

  tag(): IPropertyEditable<string> {
    return this._tag;
  }

  private readonly _inOutType: IPropertyEditable<string>;

  inOutType(): IPropertyEditable<string> {
    return this._inOutType;
  }

  private readonly _referenceFiles: IPropertyEditable<IEditableFormatReferenceFile[]>;

  referenceFiles(): IPropertyEditable<IEditableFormatReferenceFile[]> {
    return this._referenceFiles;
  }

  private readonly _lastMaxObjectId: IPropertyEditable<number>;

  lastMaxObjectId(): IPropertyEditable<number> {
    return this._lastMaxObjectId;
  }

  private readonly _maxPageKey: IPropertyPresentable<number>;

  maxPageKey(): IPropertyPresentable<number> {
    return this._maxPageKey;
  }

  private readonly _writeMethodType: IPropertyEditable<WriteMethodKinds>;

  writeMethodType(): IPropertyEditable<WriteMethodKinds> {
    return this._writeMethodType;
  }

  private readonly _settings: IPropertyEditable<IEditableFormatSetting>;

  settings(): IPropertyEditable<IEditableFormatSetting> {
    return this._settings;
  }

  private readonly _questionnaire: IPropertyEditable<IEditableFormatQuestionnaire>;

  questionnaire(): IPropertyEditable<IEditableFormatQuestionnaire> {
    return this._questionnaire;
  }

  private _alerts: IFormatAlert[] = [];

  getAlerts(): IFormatAlert[] {
    return this._alerts;
  }

  setAlerts(value: IFormatAlert[]): void {
    this._alerts = value;
  }

  sortByLocation(): void {
    const pages = this.getPages();
    pages.forEach(p => p.sortByLocation());
    this.updatePages(pages);
  }

  clone(): FormatStructureEditor {
    return new FormatStructureEditor(this);
  }
}
